<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <exo-input-name
          alignWithParametricField
          :root="_self"
          gt_ParamOrbitTransfert1-v-step="10"
        />
        <exo-input-parametric
          :root="_self"
          name="thrust"
          gt_ParamOrbitTransfert1-v-step="11"
        />
        <exo-input-parametric
          :root="_self"
          name="isp"
          gt_ParamOrbitTransfert1-v-step="12"
        />
        <exo-input-parametric
          :root="_self"
          name="power"
          tooltip="Power required to operate the propulsion system"
          gt_ParamOrbitTransfert1-v-step="13"
        />
        <exo-input-parametric :root="_self" name="standbyPower">
          <exo-help-dialog title="Standby power">
            The standby power is typically consumed when a propulsion system
            needs to stay warm between two burns, which means that electrical
            power is consumed even though no thrust is produced.
          </exo-help-dialog>
        </exo-input-parametric>
        <!--
        <exo-input-parametric :root="_self" name="warmUpDuration" >
          <exo-help-dialog title="Thruster warm-up">
              Before being able to operate effectively, an electrical thruster may need a warm-up phase.
              During this phase, electrical power is consumed even though no thrust is produced.
              <br>
              The user specifies the warm-up power <katex-element expression="P_\mathrm{wu}"/> expressed in Watt
              and the warm-up duration <katex-element expression="T_\mathrm{wu}"/> in minutes.
          </exo-help-dialog>
        </exo-input-parametric>
        <exo-input-parametric :root="_self" name="warmUpPower" />
        -->
        <v-divider></v-divider>
        <exo-input-parametric
          :root="_self"
          name="totalMass"
          gt_ParamOrbitTransfert1-v-step="14"
        />
        <exo-input-select
          alignWithParametricField
          :root="_self"
          name="propellantCapacityChoice"
          gt_ParamOrbitTransfert1-v-step="15"
        >
          <exo-help-dialog title="Propellant capacity" margin="mt-6 ml-2">
            The propellant capacity can be specified in terms of propellant mass
            or in terms of total impulse. An error is raised if this limit is
            reached during a manoeuvre.
            <br />
            <br />
            It is also possible to choose an unlimited propellant capacity.
            Although non physical, this setting allows to compute the propellant
            capacity that a spacecraft would need to accomplish any manoeuvre.
          </exo-help-dialog>
        </exo-input-select>
        <exo-input-parametric :root="_self" name="propellantMass" />
        <exo-input-parametric :root="_self" name="totalImpulse" />
        <v-divider></v-divider>
        <exo-input-parametric
          :root="_self"
          name="cost"
          tooltip="Used to compute the cost of the mission"
          gt_ParamOrbitTransfert1-v-step="16"
        />
        <exo-input-checkbox
          :root="_self"
          :forceDisplay="true"
          name="attitudeDynamicsModelDefined"
          customLabel="Define position and orientation (used in ADCS Design mission only)"
        />
        <exo-card-form
          v-if="inputs.attitudeDynamicsModelDefined"
          label="Position in spacecraft frame"
        >
          <v-row no-gutters justify="start">
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="positionInSpacecraftFrameX"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="positionInSpacecraftFrameY"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="positionInSpacecraftFrameZ"
              />
            </v-col>
          </v-row>
        </exo-card-form>
        <exo-card-form
          v-if="inputs.attitudeDynamicsModelDefined"
          label="Direction in spacecraft frame"
        >
          <template v-slot:titleTooltip>
            <exo-help-dialog
              title="Propulsion system direction in spacecraft frame"
              :margin="'m-0'"
            >
              <tooltipPropulsionSystemDirection />
            </exo-help-dialog>
          </template>
          <v-row no-gutters justify="start">
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameX"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameY"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameZ"
              />
            </v-col>
          </v-row>
        </exo-card-form>
        <exo-form-alert
          type="error"
          v-if="
            Number(inputs.directionInSpacecraftFrameX) === 0 &&
            Number(inputs.directionInSpacecraftFrameY) === 0 &&
            Number(inputs.directionInSpacecraftFrameZ) === 0 &&
            inputs.attitudeDynamicsModelDefined
          "
        >
          The norm of the direction vector must be positive
        </exo-form-alert>
        <div gt_ParamOrbitTransfert1-v-step="17"></div>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
    <v-tour
      name="paramOrbitalTransfer1TourAnaPS"
      :steps="steps_analytical_propulsion_system"
      :callbacks="customCallbacks"
    ></v-tour>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import analyticalMixin from '../../mixins/analyticalFoms.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataTools from '../../tools/data.js'
import tooltipPropulsionSystemDirection from '../../components/Tooltips/tooltipPropulsionSystemDirection'
import { mapGetters } from 'vuex'
import guidedTutorialParamOrbitalTransfer1 from '../../components/GuidedTutorials/guidedTutorialParamOrbitalTransfer1.js'
import { steps_analytical_propulsion_system } from '../../components/GuidedTutorials/guidedTutorialParamOrbitalTransfer1.js'

export default {
  name: 'analyticalthruster',
  mixins: [formMixin, analyticalMixin],

  components: {
    tooltipPropulsionSystemDirection
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'positionInSpacecraftFrameX',
          text:
            'X position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameX'),
          sortable: true
        },
        {
          value: 'positionInSpacecraftFrameY',
          text:
            'Y position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameY'),
          sortable: true
        },
        {
          value: 'positionInSpacecraftFrameZ',
          text:
            'Z position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameZ'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameX',
          text:
            'X direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameX'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameY',
          text:
            'Y direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameY'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameZ',
          text:
            'Z direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameZ'),
          sortable: true
        },
        {
          value: 'thrust',
          text: 'Thrust' + this.getDefaultUnitLabel('thrust'),
          sortable: true
        },
        {
          value: 'isp',
          text: 'Isp' + this.getDefaultUnitLabel('isp'),
          sortable: true
        },
        {
          value: 'power',
          text: 'Power' + this.getDefaultUnitLabel('power'),
          sortable: true
        },
        {
          value: 'totalMass',
          text: 'Total mass' + this.getDefaultUnitLabel('totalMass'),
          sortable: true
        },
        {
          value: 'propellantMass',
          text: 'Propellant' + this.getDefaultUnitLabel('propellantMass'),
          sortable: true
        },
        {
          value: 'totalImpulse',
          text: 'Total impulse' + this.getDefaultUnitLabel('totalImpulse'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      enumPropertiesList: this.getEnumProperties(),

      modalRef: { content: {} },
      attitudeDynamicsModelDefined: false,
      showModal: false,
      steps_analytical_propulsion_system: steps_analytical_propulsion_system,
      customCallbacks: {
        onNextStep: (currentStep) => {
          guidedTutorialParamOrbitalTransfer1.onNextStepAnaPS(currentStep, this)
        },
        onSkip: () => {
          guidedTutorialParamOrbitalTransfer1.onSkipAnaPS(this)
        },
        onFinish: () => {
          this.submitForm()
        }
      },
      inputs: {
        positionInSpacecraftFrameX: this.getDefaultValue(
          'positionInSpacecraftFrameX'
        ),
        positionInSpacecraftFrameY: this.getDefaultValue(
          'positionInSpacecraftFrameY'
        ),
        positionInSpacecraftFrameZ: this.getDefaultValue(
          'positionInSpacecraftFrameZ'
        ),
        directionInSpacecraftFrameX: this.getDefaultValue(
          'directionInSpacecraftFrameX'
        ),
        directionInSpacecraftFrameY: this.getDefaultValue(
          'directionInSpacecraftFrameY'
        ),
        directionInSpacecraftFrameZ: this.getDefaultValue(
          'directionInSpacecraftFrameZ'
        ),
        thrust: this.getDefaultValue('thrust'),
        isp: this.getDefaultValue('isp'),
        power: this.getDefaultValue('power'),
        propellantCapacityChoice: this.getDefaultValue(
          'propellantCapacityChoice'
        ),
        totalMass: this.getDefaultValue('totalMass'),
        propellantMass: this.getDefaultValue('propellantMass'),
        totalImpulse: this.getDefaultValue('totalImpulse'),
        cost: this.getDefaultValue('cost'),
        standbyPower: this.getDefaultValue('standbyPower'),
        warmUpPower: this.getDefaultValue('warmUpPower'),
        warmUpDuration: this.getDefaultValue('warmUpDuration'),
        parametricValues: {
          positionInSpacecraftFrameX: this.buildDefaultParametricValue(
            'positionInSpacecraftFrameX'
          ),
          positionInSpacecraftFrameY: this.buildDefaultParametricValue(
            'positionInSpacecraftFrameY'
          ),
          positionInSpacecraftFrameZ: this.buildDefaultParametricValue(
            'positionInSpacecraftFrameZ'
          ),
          directionInSpacecraftFrameX: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameX'
          ),
          directionInSpacecraftFrameY: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameY'
          ),
          directionInSpacecraftFrameZ: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameZ'
          ),
          thrust: this.buildDefaultParametricValue('thrust'),
          isp: this.buildDefaultParametricValue('isp'),
          power: this.buildDefaultParametricValue('power'),
          standbyPower: this.buildDefaultParametricValue('standbyPower'),
          warmUpPower: this.buildDefaultParametricValue('warmUpPower'),
          warmUpDuration: this.buildDefaultParametricValue('warmUpDuration'),
          propellantMass: this.buildDefaultParametricValue('propellantMass'),
          totalMass: this.buildDefaultParametricValue('totalMass'),
          totalImpulse: this.buildDefaultParametricValue('totalImpulse'),
          cost: this.buildDefaultParametricValue('cost')
        }
      }
    }
  },

  mounted: function () {
    if (this.gt_OrbitTransferParam1.isActive) {
      this.$tours['paramOrbitalTransfer1TourAnaPS'].start()
      this.gt_OrbitTransferParam1.thrusterTourDone = true
    }
  },
  computed: {
    ...mapGetters(['gt_OrbitTransferParam1'])
  },

  methods: {
    getCurrentFormType() {
      return 'ParametricPropulsionSystemDefinition'
    },

    getRequiredPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'required'
      )
      return requiredList
    },

    getOptionalPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    getSubTypes(instance) {
      let types = {
        propellantCapacityChoice: instance.propellantCapacityChoice
      }
      return types
    },

    getFieldState(parentObject, fieldName, required) {
      let fieldState = analyticalMixin.methods.getFieldState.call(
        this,
        parentObject,
        fieldName,
        required
      )
      // Check if norm is correct
      if (JSON.stringify(fieldName).includes("directionInSpacecraftFrame") && this.inputs.attitudeDynamicsModelDefined && this.inputs.directionInSpacecraftFrameX == 0 
      && this.inputs.directionInSpacecraftFrameY == 0 && this.inputs.directionInSpacecraftFrameZ == 0
      ) {
          fieldState.value = false
          fieldState.message =
            'Sorry, the norm of the direction must be above 0.'
        }
      return fieldState
    },

    getRefList(param) {
      return []
    }
  }
}
</script>
